/**
 * Texas Hold'em cash-table rules shared by the table engine and the web client.
 *
 * Stakes and table size match the legacy `poker/` app: 9-max, blinds 10/20, 30s
 * turn clock, at most three raises per street. Hand ranking is real 7-card
 * Hold'em (the old Node process never shipped `utils/gameUtils.js`). Winners are
 * always computed here — the client never reports them.
 */

import {
  BJ_RANKS,
  BJ_SUITS,
  shuffleCards,
  type BjBonusStatus,
  type BjCard,
  type BjChatMessage,
  type BjPublicCard,
} from "./blackjack.js";

export type { BjCard, BjChatMessage, BjPublicCard };

export const PK = {
  MAX_SEATS: 9,
  SMALL_BLIND: 10,
  BIG_BLIND: 20,
  TURN_MS: 30_000,
  HAND_END_MS: 5_000,
  MAX_RAISES_PER_STREET: 3,
  MAX_CHAT_LENGTH: 200,
  MAX_CHAT_HISTORY: 150,
  MIN_CHIPS_TO_SIT: 20,
} as const;

export type PkAction = "fold" | "check" | "call" | "raise" | "allin";
export type PkStreet = "preflop" | "flop" | "turn" | "river";
export type PkPhase = "waiting" | PkStreet | "showdown" | "hand_end";

export type PkHandRank =
  | "high_card"
  | "pair"
  | "two_pair"
  | "three_of_a_kind"
  | "straight"
  | "flush"
  | "full_house"
  | "four_of_a_kind"
  | "straight_flush";

export const PK_HAND_CATEGORY: Record<PkHandRank, number> = {
  high_card: 0,
  pair: 1,
  two_pair: 2,
  three_of_a_kind: 3,
  straight: 4,
  flush: 5,
  full_house: 6,
  four_of_a_kind: 7,
  straight_flush: 8,
};

export const PK_HAND_LABEL: Record<PkHandRank, string> = {
  high_card: "Wysoka karta",
  pair: "Para",
  two_pair: "Dwie pary",
  three_of_a_kind: "Trójka",
  straight: "Strit",
  flush: "Kolor",
  full_house: "Full",
  four_of_a_kind: "Kareta",
  straight_flush: "Poker",
};

export const PK_PHASE_LABEL: Record<PkPhase, string> = {
  waiting: "Czekamy na graczy",
  preflop: "Preflop",
  flop: "Flop",
  turn: "Turn",
  river: "River",
  showdown: "Showdown",
  hand_end: "Koniec rozdania",
};

export const PK_ACTION_LABEL: Record<PkAction, string> = {
  fold: "Pas",
  check: "Check",
  call: "Wyrównaj",
  raise: "Podbij",
  allin: "All-in",
};

export const PK_STREET_ORDER: readonly PkStreet[] = ["preflop", "flop", "turn", "river"];

export interface PkHandValue {
  rank: PkHandRank;
  category: number;
  kickers: readonly number[];
  name: string;
}

export interface PkSidePot {
  amount: number;
  eligibleUserIds: readonly number[];
}

export interface PkContributor {
  userId: number;
  totalBet: number;
  folded: boolean;
}

export interface PkActionContext {
  isYourTurn: boolean;
  phase: PkPhase;
  folded: boolean;
  allIn: boolean;
  sittingOut: boolean;
  chips: number;
  streetBet: number;
  currentBet: number;
  raisesThisStreet: number;
}

export interface PkPublicPlayer {
  userId: number;
  name: string;
  seatIndex: number;
  chips: number;
  streetBet: number;
  folded: boolean;
  allIn: boolean;
  sittingOut: boolean;
  isDealer: boolean;
  isSmallBlind: boolean;
  isBigBlind: boolean;
  currentTurn: boolean;
  holeCards: BjPublicCard[];
  keys: number;
  skinId: string;
  handName?: string;
}

export interface PkWaitingEntry {
  userId: number;
  name: string;
  chips: number;
  keys: number;
}

export interface PkWinner {
  userId: number;
  name: string;
  amount: number;
  handName: string;
}

export interface PkGameUpdate {
  phase: PkPhase;
  pot: number;
  currentBet: number;
  communityCards: BjCard[];
  players: PkPublicPlayer[];
  waitingList: PkWaitingEntry[];
  currentUserId: number | null;
  dealerSeatIndex: number | null;
  seatedCount: number;
  turnEndsAt: number | null;
  handEndsAt: number | null;
  toCall: number;
  minRaiseTo: number;
  legalActions: PkAction[];
  winners: PkWinner[];
}

export type PkSession = BjBonusStatus & { seated: boolean };

export function pokerRank(card: BjCard): number {
  if (card.value === "A") return 14;
  if (card.value === "K") return 13;
  if (card.value === "Q") return 12;
  if (card.value === "J") return 11;
  return Number.parseInt(card.value, 10) || 0;
}

export function createPokerDeck(random = Math.random): BjCard[] {
  const deck: BjCard[] = [];
  for (const suit of BJ_SUITS) {
    for (const value of BJ_RANKS) {
      deck.push({ suit, value });
    }
  }
  return shuffleCards(deck, random);
}

export function handDisplayName(rank: PkHandRank, kickers: readonly number[]): string {
  if (rank === "straight_flush" && kickers[0] === 14) {
    return "Poker królewski";
  }
  return PK_HAND_LABEL[rank];
}

function rankCounts(ranks: readonly number[]): Map<number, number> {
  const counts = new Map<number, number>();
  for (const rank of ranks) {
    counts.set(rank, (counts.get(rank) ?? 0) + 1);
  }
  return counts;
}

function groupsOf(counts: Map<number, number>, size: number): number[] {
  const ranks: number[] = [];
  for (const [rank, count] of counts) {
    if (count === size) ranks.push(rank);
  }
  ranks.sort((a, b) => b - a);
  return ranks;
}

function leftoverKickers(ranks: readonly number[], used: ReadonlySet<number>, take: number): number[] {
  const kickers: number[] = [];
  for (const rank of ranks) {
    if (used.has(rank)) continue;
    kickers.push(rank);
    if (kickers.length >= take) break;
  }
  return kickers;
}

function straightHigh(uniqueDesc: readonly number[]): number | null {
  if (uniqueDesc.length < 5) return null;
  for (let i = 0; i <= uniqueDesc.length - 5; i += 1) {
    const head = uniqueDesc[i];
    if (head === undefined) continue;
    let ok = true;
    for (let step = 1; step < 5; step += 1) {
      if (uniqueDesc[i + step] !== head - step) {
        ok = false;
        break;
      }
    }
    if (ok) return head;
  }
  const wheel = [14, 5, 4, 3, 2];
  if (wheel.every((rank) => uniqueDesc.includes(rank))) return 5;
  return null;
}

export function evaluateFive(cards: readonly BjCard[]): PkHandValue {
  const ranks = cards.map(pokerRank).sort((a, b) => b - a);
  const suits = cards.map((card) => card.suit);
  const flush = suits.length > 0 && suits.every((suit) => suit === suits[0]);
  const unique = [...new Set(ranks)].sort((a, b) => b - a);
  const highStraight = straightHigh(unique);
  const counts = rankCounts(ranks);
  const quads = groupsOf(counts, 4);
  const trips = groupsOf(counts, 3);
  const pairs = groupsOf(counts, 2);

  let rank: PkHandRank = "high_card";
  let kickers: number[] = ranks.slice();

  if (flush && highStraight !== null) {
    rank = "straight_flush";
    kickers = [highStraight];
  } else if (quads[0] !== undefined) {
    rank = "four_of_a_kind";
    kickers = [quads[0], ...leftoverKickers(ranks, new Set(quads), 1)];
  } else if (trips[0] !== undefined && (pairs[0] !== undefined || trips[1] !== undefined)) {
    rank = "full_house";
    const pairRank = pairs[0] ?? trips[1] ?? 0;
    kickers = [trips[0], pairRank];
  } else if (flush) {
    rank = "flush";
    kickers = ranks.slice();
  } else if (highStraight !== null) {
    rank = "straight";
    kickers = [highStraight];
  } else if (trips[0] !== undefined) {
    rank = "three_of_a_kind";
    kickers = [trips[0], ...leftoverKickers(ranks, new Set(trips), 2)];
  } else if (pairs.length >= 2 && pairs[0] !== undefined && pairs[1] !== undefined) {
    rank = "two_pair";
    kickers = [pairs[0], pairs[1], ...leftoverKickers(ranks, new Set([pairs[0], pairs[1]]), 1)];
  } else if (pairs[0] !== undefined) {
    rank = "pair";
    kickers = [pairs[0], ...leftoverKickers(ranks, new Set(pairs), 3)];
  }

  const category = PK_HAND_CATEGORY[rank];
  return { rank, category, kickers, name: handDisplayName(rank, kickers) };
}

function chooseFive(cards: readonly BjCard[]): BjCard[][] {
  const n = cards.length;
  if (n <= 5) return [cards.slice() as BjCard[]];
  const out: BjCard[][] = [];
  for (let a = 0; a < n; a += 1) {
    for (let b = a + 1; b < n; b += 1) {
      for (let c = b + 1; c < n; c += 1) {
        for (let d = c + 1; d < n; d += 1) {
          for (let e = d + 1; e < n; e += 1) {
            const ca = cards[a];
            const cb = cards[b];
            const cc = cards[c];
            const cd = cards[d];
            const ce = cards[e];
            if (ca && cb && cc && cd && ce) out.push([ca, cb, cc, cd, ce]);
          }
        }
      }
    }
  }
  return out;
}

export function compareHands(a: PkHandValue, b: PkHandValue): number {
  if (a.category !== b.category) return a.category - b.category;
  const len = Math.max(a.kickers.length, b.kickers.length);
  for (let i = 0; i < len; i += 1) {
    const left = a.kickers[i] ?? 0;
    const right = b.kickers[i] ?? 0;
    if (left !== right) return left - right;
  }
  return 0;
}

/** Best 5-card poker hand from 5–7 cards (hole + board). */
export function evaluateBest(cards: readonly BjCard[]): PkHandValue {
  let best: PkHandValue | null = null;
  for (const five of chooseFive(cards)) {
    const value = evaluateFive(five);
    if (!best || compareHands(value, best) > 0) best = value;
  }
  return best ?? evaluateFive(cards);
}

export function nextStreet(street: PkStreet): PkStreet | "showdown" {
  if (street === "preflop") return "flop";
  if (street === "flop") return "turn";
  if (street === "turn") return "river";
  return "showdown";
}

/**
 * Standard Hold'em button / blinds in an ordered ring where index 0 is the dealer.
 * Heads-up: dealer posts the small blind. 3+: small blind is left of the button.
 */
export function blindIndexes(count: number): { sb: number; bb: number } {
  if (count < 2) return { sb: 0, bb: 0 };
  if (count === 2) return { sb: 0, bb: 1 };
  return { sb: 1, bb: 2 };
}

/** Index in a dealer-first ring of the first player to act on a street. */
export function firstToActIndex(street: PkStreet, count: number): number {
  if (count < 2) return 0;
  if (street === "preflop") {
    if (count === 2) return 0;
    return 3 % count;
  }
  return 1 % count;
}

export function toCallAmount(streetBet: number, currentBet: number): number {
  return Math.max(0, currentBet - streetBet);
}

export function minRaiseToAmount(currentBet: number): number {
  return currentBet + PK.BIG_BLIND;
}

export function allowedPokerActions(ctx: PkActionContext): PkAction[] {
  if (!ctx.isYourTurn) return [];
  if (ctx.phase === "waiting" || ctx.phase === "showdown" || ctx.phase === "hand_end") return [];
  if (ctx.folded || ctx.allIn || ctx.sittingOut) return [];
  if (ctx.chips <= 0) return [];

  const toCall = toCallAmount(ctx.streetBet, ctx.currentBet);
  const actions: PkAction[] = ["fold"];

  if (toCall === 0) {
    actions.push("check");
  } else if (ctx.chips > toCall) {
    actions.push("call");
  }

  const minTo = minRaiseToAmount(ctx.currentBet);
  const minAdd = minTo - ctx.streetBet;
  if (
    ctx.raisesThisStreet < PK.MAX_RAISES_PER_STREET &&
    ctx.chips > toCall &&
    ctx.chips >= minAdd
  ) {
    actions.push("raise");
  }

  if (toCall === 0 || ctx.chips !== toCall) {
    actions.push("allin");
  } else if (!actions.includes("call")) {
    actions.push("allin");
  }

  return actions;
}

/**
 * Side pots from committed chips. Folded players still feed the pot but cannot win it.
 * Empty eligible sets (everyone folded into a slice) are dropped — the engine awards
 * that money with the previous slice's winner in practice by merging into the main pot
 * of still-live players; callers should skip pots with no eligible players.
 */
export function buildSidePots(contributors: readonly PkContributor[]): PkSidePot[] {
  const levels = [...new Set(contributors.map((row) => row.totalBet).filter((n) => n > 0))].sort(
    (a, b) => a - b,
  );
  const pots: PkSidePot[] = [];
  let previous = 0;
  for (const level of levels) {
    const inLevel = contributors.filter((row) => row.totalBet >= level);
    const amount = (level - previous) * inLevel.length;
    const eligibleUserIds = inLevel.filter((row) => !row.folded).map((row) => row.userId);
    if (amount > 0 && eligibleUserIds.length > 0) {
      pots.push({ amount, eligibleUserIds });
    }
    previous = level;
  }
  return pots;
}

export function splitPot(amount: number, winnerCount: number): number[] {
  if (winnerCount <= 0) return [];
  const base = Math.floor(amount / winnerCount);
  let remainder = amount % winnerCount;
  return Array.from({ length: winnerCount }, () => {
    const extra = remainder > 0 ? 1 : 0;
    remainder -= extra;
    return base + extra;
  });
}

export function publicHoleCards(cards: readonly BjCard[], reveal: boolean): BjPublicCard[] {
  if (cards.length === 0) return [];
  if (reveal) return cards.map((card) => ({ suit: card.suit, value: card.value }));
  return cards.map(() => ({ hidden: true as const }));
}

export function isBettingStreet(phase: PkPhase): phase is PkStreet {
  return phase === "preflop" || phase === "flop" || phase === "turn" || phase === "river";
}
